import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';

import { faEdit, faFileDownload } from '@fortawesome/pro-duotone-svg-icons';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Box, Button, Chip, Container, useTheme } from '@mui/material';

import { claimQueries, downloadExtractClaimFile } from '@repo/api/claim/claims';
import { useUser } from '@repo/auth';
import { DataDisplay } from '@repo/features/dataDisplay';
import { StatusBox } from '@repo/ui/StatusBox';
import { downloadFile } from '@repo/ui/utils/download';

import { Route as EditRoute } from '~pages/_claims/claim.$claimId/(fnol)/edit.$page';
import { Inactivity } from './-components/Inactivity';

export const Route = createFileRoute('/_claims/claim/$claimId/_claim/')({
	beforeLoad: ({ context }) => {
		if (!context.user?.permissions.includes('claim.view')) {
			throw redirect({
				to: '/',
			});
		}
	},
	loader: ({ context, params }) => {
		void context.queryClient.ensureQueryData(claimQueries.id(params.claimId));
	},
	component: function ClaimDetailsTab() {
		const { claimId } = Route.useParams();

		const { data: claim } = useSuspenseQuery(claimQueries.id(claimId));

		const navigate = Route.useNavigate();

		const theme = useTheme();

		const { permissions } = useUser();

		return (
			<Container maxWidth={false} sx={{ marginLeft: 0 }}>
				<Box
					sx={{
						display: 'flex',
						gap: 2,
						alignItems: 'flex-start',
					}}
				>
					<Box sx={{ flexGrow: 1 }}>
						<StatusBox
							label="Claim Details"
							color={
								claim.fnolCompleted ?
									theme.palette.success
								:	theme.palette.yellow
							}
							bgcolor={
								claim.fnolCompleted ?
									theme.palette.green.light
								:	theme.palette.yellow.light
							}
						>
							{claim.fnolCompleted ?
								<Chip
									label="Completed"
									size="small"
									sx={({ palette }) => ({
										borderWidth: 1,
										borderStyle: 'solid',
										color: palette.success.dark,
										backgroundColor: 'transparent',
										'& .MuiChip-icon': {
											height: '0.7em',
											marginLeft: '8px',
										},
									})}
									icon={<FontAwesomeIcon icon={faCheck} color="currentColor" />}
								/>
							:	<Chip
									label="Draft"
									size="small"
									sx={{
										color: 'offwhite.contrastText',
										borderWidth: 1,
										borderStyle: 'solid',
										backgroundColor: 'offwhite.main',
									}}
								/>
							}
							{permissions.includes('claim.edit') && (
								<Button
									disabled={claim.stage === 'Closed'}
									variant="outlined"
									size="small"
									color="button"
									startIcon={<FontAwesomeIcon icon={faEdit} />}
									sx={{
										'& .MuiButton-startIcon svg': {
											fontSize: '1rem',
										},
									}}
									onClick={() => {
										void navigate({
											to: EditRoute.to,
											params: (params) => ({
												...params,
												page: 'insured',
											}),
										});
									}}
								>
									Edit
								</Button>
							)}
						</StatusBox>
					</Box>
					<Inactivity />
					{permissions.includes('claim.extract.details') && (
						<Button
							variant="outlined"
							startIcon={<FontAwesomeIcon icon={faFileDownload} />}
							onClick={() => {
								void downloadExtractClaimFile(claim.claimId, 'details').then(
									downloadFile,
								);
							}}
						>
							Extract Details
						</Button>
					)}
				</Box>
				<DataDisplay data={claim.claimDetails} />
			</Container>
		);
	},
	head: (ctx) => ctx.match.context.setTitle('Claim', 'Details'),
});
